/**
 * Maps the server's member-management errors to inline copy for the Members tab.
 *
 * Pure and dependency-light, so it is unit-testable without React. The backend
 * (`server/app/api/partner_members.py`) returns specific statuses:
 *   - POST 409 ("member already in this org"): already a member.
 *   - POST 422: invalid email shape, or an unparseable optional phone. The
 *     Pydantic detail names the failing field, so the copy follows it.
 *   - PATCH or DELETE 403 (`OWNER_FIXED_DETAIL`): the owner's row. A backstop
 *     only, since the UI shows no controls on it; other 403s carry other
 *     details and fall through to them.
 *   - PATCH or DELETE 400 (self-target): a backstop only, since the UI disables
 *     the caller's own row controls.
 *   - PATCH or DELETE 404: the member is no longer in the org.
 *
 * A 401 is not mapped: `useApi` clears the session and the redirect takes over.
 * Any other `ApiError` shows the server `detail`, another `Error` its message,
 * and anything else a generic retry message.
 */
import { ApiError } from "@dearbill/shared";

/** The 403 detail of a role change or remove that targets the org's owner. */
const OWNER_FIXED_DETAIL = "owner role is fixed";

function isOwnerFixed(err: ApiError): boolean {
  return err.status === 403 && err.detail === OWNER_FIXED_DETAIL;
}

/** Map a failed invite (`POST .../members`) to inline copy. */
export function inviteErrorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 409) return "That person is already a member of this org.";
    if (err.status === 422) {
      // The raw Pydantic detail names the failing field in each entry's `loc`;
      // `err.detail` is its stringified form, useless for arrays of objects.
      return JSON.stringify(err.body).includes("phone")
        ? "Please enter a valid mobile number, or leave it blank."
        : "Please enter a valid email.";
    }
    return err.detail;
  }
  if (err instanceof Error) return err.message;
  return "We couldn't send the invitation. Please try again.";
}

/** Map a failed role change (`PATCH .../members/{id}`) to inline copy. */
export function roleChangeErrorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    if (isOwnerFixed(err)) return "The owner's role can't be changed.";
    if (err.status === 400) return "You can't change your own role.";
    if (err.status === 404) return "That member is no longer in this org.";
    return err.detail;
  }
  if (err instanceof Error) return err.message;
  return "We couldn't change the role. Please try again.";
}

/** Map a failed remove (`DELETE .../members/{id}`) to inline copy. */
export function removeErrorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    if (isOwnerFixed(err)) return "The owner can't be removed.";
    if (err.status === 400) return "You can't remove yourself.";
    if (err.status === 404) return "That member is no longer in this org.";
    return err.detail;
  }
  if (err instanceof Error) return err.message;
  return "We couldn't remove this member. Please try again.";
}
